import { useEffect, useState } from 'react';
import DimensionNPSDisplay from '../DimensionNPSDisplay';
import TimeRangeSelector from '../TimeRangeSelector';
import { apiPost } from '../../services/apiClient';

function npsBadgeClass(nps, count) {
    if (count < 10) return 'muted';
    if (nps >= 50) return 'positive';
    if (nps <= -10) return 'negative';
    return 'neutral';
}

/**
 * Summary card: insight-first — headline review count, top 3 items, footer CTA
 */
function MostReviewedSummary({ data, totalData, dimConfig, onClick, error }) {
    if (error) {
        return (
            <div className="dim-widget-error">
                <div>Unable to load review data.</div>
            </div>
        );
    }

    const totalReviews = totalData.reduce((sum, r) => sum + r.count, 0);

    return (
        <div
            className="dim-widget-card most-reviewed"
            onClick={onClick}
            role="region"
            aria-label="Most Reviewed"
        >
            <div className="dim-widget-header">
                <span className="widget-icon">📊</span>
                Most Reviewed
            </div>
            <div className="dim-widget-headline">
                {totalReviews.toLocaleString()} reviews
            </div>
            <div className="dim-widget-subtitle">
                across {totalData.length} {dimConfig.plural}
            </div>
            <div className="dim-widget-body">
                {data.length > 0 ? (
                    <ul className="dim-widget-list">
                        {data.map(row => (
                            <li key={row.dimension_value}>
                                <span className="item-name">{row.dimension_value}</span>
                                <span className="item-count">{row.count.toLocaleString()}</span>
                                <span className={`nps-badge ${npsBadgeClass(row.nps, row.count)}`}>
                                    {row.nps}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="dim-widget-empty">
                        No reviews found for this dimension.
                    </div>
                )}
            </div>
            <div className="dim-widget-footer">
                <button
                    aria-expanded={false}
                    onClick={(e) => { e.stopPropagation(); onClick(); }}
                >
                    View all {totalData.length} {dimConfig.plural} →
                </button>
            </div>
        </div>
    );
}

/**
 * Detail view: DimensionNPSDisplay with its own TimeRangeSelector
 */
function MostReviewedDetail({ data: initialData, dimension, topicId, dimConfig, isLoading, onClose }) {
    const [timeRange, setTimeRange] = useState('all');
    const [filteredData, setFilteredData] = useState(initialData);
    const [isFilterLoading, setIsFilterLoading] = useState(false);

    useEffect(() => {
        if (timeRange === 'all') {
            setFilteredData(initialData);
            setIsFilterLoading(false);
            return;
        }

        let isCancelled = false;

        setIsFilterLoading(true);
        apiPost('topic/dimension/all', {
            topic_id: topicId,
            dimension,
            period: timeRange,
        })
            .then(r => r.json())
            .then(result => {
                if (isCancelled) return;
                const nps = result.nps || [];
                nps.sort((a, b) => b.count - a.count);
                setFilteredData(nps);
            })
            .catch(err => {
                if (isCancelled) return;
                console.error('[MostReviewedDetail] fetch error:', err);
            })
            .finally(() => {
                if (isCancelled) return;
                setIsFilterLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [initialData, timeRange, topicId, dimension]);

    return (
        <>
            <div className="dim-detail-header">
                <h3>Most Reviewed {dimConfig.plural}</h3>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <TimeRangeSelector
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        periods={['30d', '90d', '6mo', '1y', 'all']}
                        periodLabels={{ '30d': '30D', '90d': '90D', '6mo': '6Mo', '1y': '1Y', 'all': 'All' }}
                        defaultPeriod="all"
                    />
                    <button className="dim-detail-close" onClick={onClose}>← Back to Overview</button>
                </div>
            </div>
            {isLoading || isFilterLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading...</div>
            ) : (
                <DimensionNPSDisplay
                    data={filteredData}
                    dimension={dimension}
                    topicId={topicId}
                />
            )}
        </>
    );
}

MostReviewedSummary.Detail = MostReviewedDetail;
export default MostReviewedSummary;
